import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateDigest } from '../../reducers/digestReducer';
import { useAuth } from '@clerk/clerk-react';
import { 
  ExternalLink, 
  BookmarkPlus, 
  ArrowLeft,
  DoorOpen,
  EyeIcon,
  Archive
} from 'lucide-react';
import { markEmailAsRead } from '../../utils/emailActions';
import { formatActionTypeLabel, formatEmailText } from '../../utils/formatter';
import { getAssistantActions, getUserActions } from '../../utils/emailActions';
import { hasRenderableEmailHtml, sanitizeEmailHtml } from '../../utils/emailContentSanitizer';
import {
  canDisplayMailAction,
  canWriteMail,
  resolveOriginalMessageLink,
} from '../../utils/mailProvider';
import PrinceGeoffrey from '../Chat/PrinceGeoffrey';
import AddToProjectModal from '../Project/AddToProjectModal';
import EmailLabelSelectOptions from '../Shared/EmailLabelSelectOptions';
import ActionDetailsModal from './ActionDetailsModal';
import useEventCallback from '../../hooks/useEventCallback';
import './EmailContent.css';

const EMAIL_IFRAME_SANDBOX = 'allow-same-origin allow-popups allow-popups-to-escape-sandbox';

const EMAIL_FRAME_BASE_STYLESHEET = "body{margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif}"
  + "img,table,video,embed,object{max-width:100% !important;height:auto !important}"
  + "table{width:auto !important}";

const DEFAULT_EMAIL_FRAME_HEIGHT = 480;
const EMAIL_FRAME_HEIGHT_BUFFER = 16;

const EMAIL_FRAME_CSP = "img-src https: data:; style-src 'unsafe-inline'; default-src 'none'";

const buildEmailFrameDocument = (sanitizedHtml) => (
  `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank">`
  + `<meta http-equiv="Content-Security-Policy" content="${EMAIL_FRAME_CSP}">`
  + `<style>${EMAIL_FRAME_BASE_STYLESHEET}</style></head><body>${sanitizedHtml}</body></html>`
);

const EmailContent = ({
  email,
  onClose,
  onArchive,
  onMoveEmail,
  onAddEmailToProject,
  onCreateTask,
  onViewTask,
  onViewProject,
  isProjectModalOpen,
  setIsProjectModalOpen,
  getActiveTask,
  getActiveProject,
  readOnlyMode,
  handleUndoAutomatedAction,
  handleAcknowledgeAction
}) => {
  const dispatch = useDispatch();
  const { getToken } = useAuth();
  
  const markEmailAsReadStarted = useRef(false);
  const emailRef = useRef(null);
  const emailFrameRef = useRef(null);

  const currentDigest = useSelector(state => state.digestList.currentDigest);
  const [isActionDetailsModalOpen, setIsActionDetailsModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [emailFrameHeight, setEmailFrameHeight] = useState(DEFAULT_EMAIL_FRAME_HEIGHT);
  const mailWriteContext = { mailbox: currentDigest?.mailbox, email };
  const visibleActions = email?.actions?.filter((action) => canDisplayMailAction(mailWriteContext, action));
  const visibleEmail = { ...email, actions: visibleActions };
  const canArchiveEmail = canWriteMail(mailWriteContext, 'archive_email');
  const canMoveEmail = canWriteMail(mailWriteContext, 'move_email');
  const canRestoreEmail = canWriteMail(mailWriteContext, 'restore_email');

  const handleMarkEmailAsRead = async (emailId) => {
    if(!markEmailAsReadStarted.current) {
      markEmailAsReadStarted.current = true;
    
      try {
        await markEmailAsRead({
          emailId,
          // Without a mailbox the server can only resolve Google ids.
          mailIntegrationId: currentDigest?.mailbox?.id,
          dispatch,
          currentDigest,
          updateDigestAction: updateDigest,
          getToken
        });
      } catch (error) {
        // Background action: a failure just leaves the email unread.
        console.error('Failed to mark email as read:', error);
      }
      markEmailAsReadStarted.current = false;
    }
  };

  const markOpenedEmailAsRead = useEventCallback(() => {
    if (email?.isUnread) {
      handleMarkEmailAsRead(email?.id);
    }
  });

  useEffect(() => {
    markOpenedEmailAsRead();
  }, [email?.id, email?.isUnread, markOpenedEmailAsRead]);

  const measureEmailFrameHeight = useCallback(() => {
    const frame = emailFrameRef.current;

    if (!frame) {
      return;
    }

    try {
      const frameDocument = frame.contentDocument;
      const measuredHeight = frameDocument?.body?.scrollHeight
        || frameDocument?.documentElement?.scrollHeight;

      if (typeof measuredHeight === 'number' && measuredHeight > 0) {
        setEmailFrameHeight(measuredHeight + EMAIL_FRAME_HEIGHT_BUFFER);
      } else {
        setEmailFrameHeight(DEFAULT_EMAIL_FRAME_HEIGHT);
      }
    } catch (error) {
      setEmailFrameHeight(DEFAULT_EMAIL_FRAME_HEIGHT);
    }
  }, []);

  const handleEmailFrameLoad = useCallback(() => {
    measureEmailFrameHeight();

    try {
      const frameImages = emailFrameRef.current?.contentDocument?.images;

      Array.from(frameImages || []).forEach((image) => {
        if (!image.complete) {
          image.addEventListener('load', measureEmailFrameHeight);
          image.addEventListener('error', measureEmailFrameHeight);
        }
      });
    } catch (error) {
    }
  }, [measureEmailFrameHeight]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    window.addEventListener('resize', measureEmailFrameHeight);

    return () => window.removeEventListener('resize', measureEmailFrameHeight);
  }, [measureEmailFrameHeight]);

  const triggerAddItemToProject = async (projectId, projectTitle) => {
    onAddEmailToProject(email, projectId, projectTitle);
  }

  const sanitizedEmailBody = email?.bodyContentType === 'html'
    ? sanitizeEmailHtml(email?.body)
    : '';
  const hasRenderableHtmlBody = hasRenderableEmailHtml(sanitizedEmailBody);
  const hasTextEmailBody = email?.bodyContentType === 'text'
    && typeof email?.body === 'string'
    && email.body.trim();
  const originalMessageLink = resolveOriginalMessageLink(email);

  const renderEmailBody = () => {
    if (email?.bodyContentType === 'html' && hasRenderableHtmlBody) {
      return (
        <iframe
          ref={emailFrameRef}
          title="Email message content"
          className="email-body-frame"
          sandbox={EMAIL_IFRAME_SANDBOX}
          srcDoc={buildEmailFrameDocument(sanitizedEmailBody)}
          onLoad={handleEmailFrameLoad}
          style={{ height: `${emailFrameHeight}px` }}
        />
      );
    }

    if (hasTextEmailBody) {
      return <div>{formatEmailText(email.body)}</div>;
    }

    return <p className="email-body-empty-state">This email has no displayable content.</p>;
  };

  const handleActionClick = (action) => {
    setSelectedAction(action);
    setIsActionDetailsModalOpen(true);
  }

  const handleArchive = () => {
    if (!canArchiveEmail) return;
    onArchive(email);
  };

  const handleMove = (destinationKey) => {
    if (!canMoveEmail) return;
    onMoveEmail(email, destinationKey);
  };

  return (
    <div className="flex items-center justify-center rounded-lg">
      <div ref={emailRef} className="flex flex-col bg-white w-full max-w-4xl max-h-[95vh] overflow-hidden">
        
        <div className="flex flex-row justify-between gap-2 items-center mb-2">
          <button
            onClick={onClose}
            data-amp-track-section="Digest Email Content"
            data-amp-track-action="Back_Click"
            data-amp-track-description="Go Back"
            aria-label="Back"
            className="email-reader-control flex items-center w-full gap-2 p-0 m-0 text-stone-600 hover:text-stone-800 bg-transparent hover:bg-transparent"
          >
            <ArrowLeft size={26} />
            <div className="flex flex-row gap-2 text-left text-md w-full">
              <DoorOpen size={22} /> 
              <p className="flex leading-tight">Back</p>
            </div>
          </button>
          {originalMessageLink.isAvailable ? (
            <a
              href={originalMessageLink.href}
              target="_blank"
              rel="noopener noreferrer"
              title={originalMessageLink.label}
              aria-label={originalMessageLink.label}
              data-amp-track-section="Digest Email Content"
              data-amp-track-action="External_Link_Click"
              data-amp-track-description={originalMessageLink.label}
              className="email-reader-control p-1 m-0 text-xs bg-transparent border-solid border border-stone-300 text-stone-500 hover:text-stone-700 hover:bg-stone-100 rounded-md transition-colors flex items-center justify-center"
            >
              <ExternalLink size={22} aria-hidden="true" />
            </a>
          ) : (
            <p className="text-xs text-stone-500">{originalMessageLink.unavailableText}</p>
          )}
        </div>

        <div>
          <h2 className="email-reader-subject text-xl font-semibold mb-0">{email.subject}</h2>
        </div>

        <div className="flex flex-col">
          <p className="email-reader-sender text-sm text-gray-600 mb-1">From: {email.from}</p>
            
          {email.actions && (
            <div className="flex flex-row items-center justify-start w-fit gap-2 my-1">
              <PrinceGeoffrey size={28} className="mb-0" />

              {getAssistantActions(visibleActions)?.map((action, actionIndex) => (
                <div key={`action-${actionIndex}`} className="text-xs whitespace-nowrap text-white bg-stone-600 rounded-md px-2 py-1">
                  <div className="cursor-pointer"
                    onClick={() => handleActionClick(action)}
                  >
                    {formatActionTypeLabel(action, currentDigest?.labels)}
                  </div>
                </div>
              ))}
              {getUserActions(visibleActions)?.map((action, actionIndex) => (
                <div className="border-l border-gray-200 pl-2">
                  <div key={`action-${actionIndex}`} className="text-xs whitespace-nowrap text-white bg-stone-600 rounded-md px-2 py-1">
                    {formatActionTypeLabel(action)}
                  </div>
                </div>
              ))}
            </div>
          )}          
        </div>

        <div className="email-reader-body border-t border-b border-gray-200 flex-grow overflow-y-auto p-4">
          <div className="email-body-container">{renderEmailBody()}</div>
        </div>
        
        <div className="flex flex-row justify-between space-x-2 pt-3 mb-2 items-center">
          <div className="flex flex-row gap-2">
            {canArchiveEmail && <button
              onClick={handleArchive}
              data-amp-track-section="Digest Email Content"
              data-amp-track-action="Archive_Click"
              data-amp-track-description="Archive Email"
              disabled={email.archived === true && email.actions?.some(action => action.type === 'archive')}
              className={`rounded-md border border-solid border-slate-200 
                ${email.archived === true && email.actions?.some(action => action.type === 'archive') ? 'opacity-50 cursor-not-allowed' : ''} flex items-center gap-1 px-3 ml-0 py-1.5 text-sm bg-transparent text-gray-600 hover:bg-gray-50 transition-colors`}
            >
              <Archive size={22} /> Archive
            </button>}

            {getActiveTask(email) ? (
              <>
                <button onClick={() => onViewTask(email)}
                  data-amp-track-section="Digest Email Content"
                  data-amp-track-action="View_Task_Click"
                  data-amp-track-description="View Task"
                  className="flex items-center gap-1 px-3 ml-0 py-1.5 text-sm rounded-md border border-solid border-slate-200 bg-transparent text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                  <EyeIcon size={22} /> View Task
                </button>
              </>
            ) : (
              <button onClick={() => onCreateTask(email)}
                data-amp-track-section="Digest Email Content"
                data-amp-track-action="Create_Task_Click"
                data-amp-track-description="Create Task"
                className="flex items-center gap-1 px-3 ml-0 py-1.5 text-sm rounded-md border border-solid border-slate-200 bg-transparent text-gray-600 hover:bg-gray-50 transition-colors"
              >
                <BookmarkPlus size={22} /> Create a Task
              </button>
            )}

            {isProjectModalOpen && (
              <AddToProjectModal setIsProjectModalOpen={setIsProjectModalOpen} addItemToProject={triggerAddItemToProject} />
            )}
          </div>
          {canMoveEmail && <div className="flex flex-row items-center gap-2">
            <select
              onChange={(e) => handleMove(e.target.value)}
              data-amp-track-section="Digest Email Content"
              data-amp-track-action="Move_Email_Click"
              data-amp-track-description="Move Email"
              defaultValue=""
              className="rounded-md border border-solid border-slate-200 text-sm p-2 w-24 cursor-pointer"
            >
              <option value="" disabled>
                {currentDigest?.destinations?.find(({ key }) => key === email.triageDestination)?.label || 'Move to...'}
              </option>
              <EmailLabelSelectOptions destinations={currentDigest?.destinations} />
            </select>
          </div>}
        </div>
      </div>

      {isActionDetailsModalOpen && (
        <ActionDetailsModal
          isOpen={isActionDetailsModalOpen}
          onClose={() => setIsActionDetailsModalOpen(false)}
          action={selectedAction}
          email={visibleEmail}
          labels={currentDigest?.labels}
          readOnlyMode={readOnlyMode}
          onRevert={selectedAction && (
            !['archive', 'move-to-label'].includes(selectedAction.type)
            || canRestoreEmail
          )
            ? () => handleUndoAutomatedAction(email.id, selectedAction.type)
            : undefined}
          onAcknowledge={handleAcknowledgeAction}
          onArchive={canArchiveEmail ? handleArchive : undefined}
          mailWritesDisabled={!canArchiveEmail}
          getEmailContent={() => setIsActionDetailsModalOpen(false)}
        />
      )}
    </div>
  );
};

export default EmailContent;
